import { DicomMetadataStore } from '@ohif/core';
import pako from 'pako';

function trimSlashes(value) {
  return (value || '').replace(/\/+$/, '');
}

function buildUrl(baseUrl, relativePath, sasToken) {
  const safeBase = trimSlashes(baseUrl);
  const safePath = (relativePath || '').replace(/^\/+/, '');
  const safeToken = (sasToken || '').replace(/^\?+/, '');
  const queryPart = safeToken ? `?${safeToken}` : '';
  return `${safeBase}/${safePath}${queryPart}`;
}

function normalizeSeriesIds(payload) {
  const rawSeriesIds = payload?.series_ids ?? payload?.series_id;

  if (Array.isArray(rawSeriesIds)) {
    return rawSeriesIds
      .map(item => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean);
  }

  if (typeof rawSeriesIds === 'string') {
    return rawSeriesIds
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  return [];
}

function decodeBlob(blobData) {
  if (!blobData) {
    throw new Error('Missing blob query parameter for annotation_server source');
  }

  let decoded;
  try {
    decoded = JSON.parse(atob(blobData));
  } catch (error) {
    throw new Error('Unable to decode annotation_server blob payload');
  }

  const blobUrl = decoded?.blob_url;
  const sasToken = decoded?.sasToken;

  if (!blobUrl || !sasToken) {
    throw new Error('Blob payload must include blob_url and sasToken');
  }

  return {
    blobUrl,
    sasToken,
    taskId: decoded?.task_id || null,
    seriesIds: normalizeSeriesIds(decoded),
    raw: decoded,
  };
}

async function fetchJson(url) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch JSON from ${url}. Status: ${response.status}`);
  }

  return response.json();
}

async function fetchGzippedJSON(url) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch gzip metadata from ${url}. Status: ${response.status}`);
  }

  const compressedData = await response.arrayBuffer();
  const decompressedData = pako.inflate(new Uint8Array(compressedData), {
    to: 'string',
  });

  return JSON.parse(decompressedData);
}

function getSeriesUidFromTagInstance(instance) {
  return instance?.['0020000E']?.Value?.[0];
}

function getStudyUidFromTagInstance(instance) {
  return instance?.['0020000D']?.Value?.[0];
}

function setCurrentStudy(study) {
  window.currentStudy = study;
  window.capturedImages = [];
}

function assignInstanceUrls(studyInstanceUID, blobUrl, sasToken) {
  const study = DicomMetadataStore.getStudy(studyInstanceUID);

  if (!study) {
    throw new Error(`Study ${studyInstanceUID} was not found in metadata store`);
  }

  study.series.forEach(series => {
    series.instances.forEach(instance => {
      instance.url = `wadouri:${buildUrl(
        blobUrl,
        `${instance.SeriesInstanceUID}/${instance.SOPInstanceUID}.dcm`,
        sasToken
      )}`;
    });
  });

  return study;
}

function shouldIncludeSeries(seriesIdSet, instance) {
  if (!seriesIdSet || seriesIdSet.size === 0) {
    return true;
  }

  const seriesUid = getSeriesUidFromTagInstance(instance);
  return !!seriesUid && seriesIdSet.has(seriesUid);
}

function addFilteredInstancesToStore(instances, seriesIdSet) {
  const filtered = instances.filter(instance => shouldIncludeSeries(seriesIdSet, instance));

  if (!filtered.length) {
    throw new Error('No instances matched the requested series_ids filter');
  }

  filtered.forEach(instance => {
    DicomMetadataStore.addInstance(instance);
  });

  return filtered;
}

async function loadFromDicomMetadataGz({ blobUrl, sasToken, seriesIdSet }) {
  const gzipUrl = buildUrl(blobUrl, 'meta/dicom_metadata.json.gz', sasToken);
  const instances = await fetchGzippedJSON(gzipUrl);

  if (!Array.isArray(instances) || instances.length === 0) {
    throw new Error('dicom_metadata.json.gz returned no instances');
  }

  const filteredInstances = addFilteredInstancesToStore(instances, seriesIdSet);
  const studyInstanceUID = getStudyUidFromTagInstance(filteredInstances[0]);

  if (!studyInstanceUID) {
    throw new Error('StudyInstanceUID missing in gzip metadata');
  }

  return studyInstanceUID;
}

async function loadFromSeriesJson({ blobUrl, sasToken, seriesIdSet }) {
  const seriesIndexUrl = buildUrl(blobUrl, 'meta/series.json', sasToken);
  const seriesIndex = await fetchJson(seriesIndexUrl);

  if (!Array.isArray(seriesIndex) || seriesIndex.length === 0) {
    throw new Error('series.json returned no series entries');
  }

  const studyInstanceUID = getStudyUidFromTagInstance(seriesIndex[0]);

  if (!studyInstanceUID) {
    throw new Error('StudyInstanceUID missing in series.json metadata');
  }

  const availableSeriesUids = seriesIndex
    .map(entry => getSeriesUidFromTagInstance(entry))
    .filter(Boolean);

  const targetSeriesUids = seriesIdSet.size
    ? availableSeriesUids.filter(seriesUid => seriesIdSet.has(seriesUid))
    : availableSeriesUids;

  if (!targetSeriesUids.length) {
    throw new Error('No matching series found in series.json for provided series_ids');
  }

  const metadataResponses = await Promise.all(
    targetSeriesUids.map(async seriesUid => {
      const metadataUrl = buildUrl(blobUrl, `meta/${seriesUid}_metadata.json`, sasToken);
      const seriesInstances = await fetchJson(metadataUrl);
      return Array.isArray(seriesInstances) ? seriesInstances : [];
    })
  );

  const flattenedInstances = metadataResponses.flat();

  if (!flattenedInstances.length) {
    throw new Error('Fallback metadata files returned no instances');
  }

  flattenedInstances.forEach(instance => {
    DicomMetadataStore.addInstance(instance);
  });

  return studyInstanceUID;
}

function notifyLoadFailure(error, servicesManager) {
  if (servicesManager?.services?.uiNotificationService) {
    servicesManager.services.uiNotificationService.show({
      title: 'Unable to load study',
      message: 'Annotation server metadata could not be loaded.',
      type: 'error',
    });
  }

  console.error(error);
}

async function loadStudyFromAnnotationServer(params, query, blobData, servicesManager) {
  const blobParam = blobData || query.get('blob');
  const decoded = decodeBlob(blobParam);

  window.azureData = decoded.raw;
  window.annotationTaskId = decoded.taskId;

  const seriesIdSet = new Set(decoded.seriesIds);

  try {
    const studyInstanceUID = await loadFromDicomMetadataGz({
      blobUrl: decoded.blobUrl,
      sasToken: decoded.sasToken,
      seriesIdSet,
    });

    const study = assignInstanceUrls(studyInstanceUID, decoded.blobUrl, decoded.sasToken);
    setCurrentStudy(study);
    window.isStillLoading = false;
    return;
  } catch (gzipError) {
    console.warn('annotation_server gzip metadata failed, trying series fallback', gzipError);
  }

  try {
    const studyInstanceUID = await loadFromSeriesJson({
      blobUrl: decoded.blobUrl,
      sasToken: decoded.sasToken,
      seriesIdSet,
    });

    const study = assignInstanceUrls(studyInstanceUID, decoded.blobUrl, decoded.sasToken);
    setCurrentStudy(study);
    window.isStillLoading = false;
  } catch (fallbackError) {
    notifyLoadFailure(fallbackError, servicesManager);
    throw fallbackError;
  }
}

export default loadStudyFromAnnotationServer;
